import { useEffect, useRef } from 'react';

import { decodeMqttText } from '@/contexts/mqtt/payload';
import { useMqtt } from '@/contexts/mqtt-provider-context';
import { mqttTopicsVideo } from '@/routes/Client/Location/pages/LocationVideo/topics';

import type { BioscopeData, VideoStreamStatus } from '../types/videoStream';

interface MqttVideoCallbacks {
    onBioscopeStatusChange: (isOnline: boolean) => void;
    onLivestreamStatusChange: (data: {
        streamId?: number;
        status: VideoStreamStatus;
        playbackId?: string;
        userId?: number;
    }) => void;
    onBioscopeDataChange: (data: BioscopeData) => void;
}

interface UseMqttVideoSubscriptionParams {
    bioscopeId?: number;
    callbacks: MqttVideoCallbacks;
}

/**
 * Hook that handles MQTT subscriptions for video streaming.
 * Separates MQTT concerns from business logic.
 */
export function useMqttVideoSubscription({
    bioscopeId,
    callbacks,
}: UseMqttVideoSubscriptionParams): { isConnected: boolean } {
    const { client, isConnected, subscribe, unsubscribe } = useMqtt();

    // Keep callbacks in ref to avoid re-subscriptions when callbacks change
    const callbacksRef = useRef(callbacks);
    callbacksRef.current = callbacks;

    useEffect(() => {
        if (!isConnected || !client || !bioscopeId) {
            return;
        }

        const handleMqttMessage = (topic: string, message: Uint8Array) => {
            try {
                const parsedMessage = JSON.parse(decodeMqttText(message));

                // Bioscope status (online/offline)
                if (topic.startsWith('bioscope/status')) {
                    if (parsedMessage.bioscopeId === bioscopeId) {
                        const isOnline = parsedMessage.status === 'online';
                        callbacksRef.current.onBioscopeStatusChange(isOnline);
                    }
                }

                // Livestream status updates
                if (topic.startsWith('bioscope/livestream')) {
                    const topicBioscopeId = Number.parseInt(
                        topic.match(/bioscope\/livestream\/(\d+)\/status/)?.[1] || '0',
                        10
                    );

                    if (topicBioscopeId === bioscopeId) {
                        callbacksRef.current.onLivestreamStatusChange({
                            streamId: parsedMessage.streamId,
                            status: parsedMessage.status || 'idle',
                            playbackId: parsedMessage.playbackId || undefined,
                            userId: parsedMessage.startedByUserId || undefined,
                        });
                    }
                }

                // Bioscope sensor data
                if (topic.startsWith('bioscope/data/response')) {
                    const topicBioscopeId = Number.parseInt(
                        topic.match(/bioscope\/data\/response\/(\d+)/)?.[1] || '0',
                        10
                    );

                    if (topicBioscopeId === bioscopeId) {
                        callbacksRef.current.onBioscopeDataChange({
                            roll: parsedMessage.Roll,
                            pitch: parsedMessage.Pitch,
                            depth: parsedMessage.Depth,
                            waterTemperature: parsedMessage['Water Temperature'],
                        });
                    }
                }
            } catch (error) {
                console.error('Error parsing MQTT message:', error);
            }
        };

        client.on('message', handleMqttMessage);

        // Subscribe to MQTT topics
        const { videoStatus, bioscopeStatus, bioboardData } = mqttTopicsVideo(bioscopeId);
        subscribe(videoStatus, { qos: 1 });
        subscribe(bioboardData, { qos: 1 });

        // Delay bioscope status subscription slightly
        const statusTimeout = setTimeout(() => {
            subscribe(bioscopeStatus, { qos: 1 });
        }, 1000);

        // Cleanup
        return () => {
            client.off('message', handleMqttMessage);
            unsubscribe(videoStatus);
            unsubscribe(bioscopeStatus);
            unsubscribe(bioboardData);
            clearTimeout(statusTimeout);
        };
    }, [isConnected, client, bioscopeId, subscribe, unsubscribe]);

    return { isConnected };
}
